import { REWARDS_ART } from '@/assets/rewards';

/**
 * The three artifacts of the Rewards Hub economy, drawn as they are in Figma
 * 13218:194697: the artwork with a pool of light behind it and the flat ellipse
 * it stands on. Both are drawn rather than baked into the asset, so the icon
 * stays crisp wherever it is used — the collection chips, the leaderboard's
 * rank tooltip, a dialog.
 */

const MEDAL = REWARDS_ART['medal.webp'];
const CUP = REWARDS_ART['cup.webp'];
const DIAMOND = REWARDS_ART['diamond.webp'];

export type ArtifactKind = 'medals' | 'cups' | 'diamonds';

/**
 * Artwork ratios against the icon box, taken from the 30px chip: the medal and
 * the cup stand full height, the diamond is squatter, and all three are 80% as
 * wide as the box is tall. Ratios rather than fixed pixels because `size` is
 * asked for at 28, 44 and 50 as well — pinned artwork simply ignored it.
 */
const ICON: Record<ArtifactKind, { src: string; height: number; width: number; glow: string }> = {
  medals: { src: MEDAL, height: 1, width: 0.8, glow: 'rgba(255,179,104,0.45)' },
  cups: { src: CUP, height: 1, width: 0.8, glow: 'rgba(255,179,104,0.45)' },
  diamonds: { src: DIAMOND, height: 0.6, width: 0.8, glow: 'rgba(57,140,255,0.5)' },
};

interface ArtifactIconProps {
  kind: ArtifactKind;
  /** Icon box height in px; the artwork, the glow and the ground shadow scale with it. */
  size?: number;
}

export function ArtifactIcon({ kind, size = 30 }: ArtifactIconProps) {
  const icon = ICON[kind];
  return (
    <span
      className="relative flex shrink-0 items-end justify-center"
      style={{ width: size, height: size }}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          width: size * 0.84,
          height: size * 0.84,
          backgroundColor: icon.glow,
          filter: `blur(${size * 0.3}px)`,
        }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2 rounded-[50%]"
        style={{
          width: size * 0.8,
          height: size * 0.13,
          backgroundColor: icon.glow,
          filter: `blur(${size * 0.08}px)`,
        }}
      />
      <img
        loading="lazy"
        decoding="async"
        src={icon.src}
        alt=""
        draggable={false}
        className="relative object-contain"
        style={{ height: size * icon.height, width: size * icon.width }}
      />
    </span>
  );
}
